import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const categoryInfo = categories.find((c) => c.slug === slug);
  const categoryName = categoryInfo ? categoryInfo.name : '全部漫画';
  const canonicalSlug = slug || 'all';

  const filteredComics = useMemo(() => {
    let result = comics;

    if (slug && slug !== 'all') {
      result = result.filter(
        (c) =>
          c.category === slug ||
          c.subCategory === slug ||
          (slug === 'completed' && c.status === 'completed')
      );
    }

    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => b.views - a.views);
    }

    return result;
  }, [slug, sortBy]);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${categoryName} - 女神漫画`,
    "description": `女神漫画${categoryName}专区，提供免费全集漫画在线阅读，支持下拉式、弹窗、翻页等多种阅读模式。海量${categoryName}资源，每日更新。`,
    "url": `https://nvshenmanhua.com/category/${canonicalSlug}`,
    "inLanguage": "zh-Hans",
    "isPartOf": {
      "@type": "WebSite",
      "name": "女神漫画",
      "url": "https://nvshenmanhua.com"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首页",
          "item": "https://nvshenmanhua.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": categoryName,
          "item": `https://nvshenmanhua.com/category/${canonicalSlug}`
        }
      ]
    }
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} - 女神漫画 | 免费全集漫画在线阅读`}
        description={`女神漫画${categoryName}专区，提供免费全集漫画在线阅读，支持下拉式、弹窗、翻页等多种阅读模式。海量${categoryName}资源，每日更新，收藏追更不迷路。`}
        keywords={`女神漫画,${categoryName},免费全集,漫画在线阅读,下拉式阅读,弹窗阅读,翻页阅读,收藏,追更`}
        canonicalUrl={`https://nvshenmanhua.com/category/${canonicalSlug}`}
        schemaJson={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">
                {categoryName}
              </span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部漫画
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="text-sm text-foreground-500 mr-2">分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  slug === cat.slug || (!slug && cat.slug === 'all')
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'popular', label: '最受欢迎' },
                { key: 'latest', label: '最近更新' },
                { key: 'rating', label: '评分最高' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}

          {filteredComics.length > 12 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium cursor-pointer">1</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">2</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">3</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-more-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}